/**
 * Custom hook for managing canvas setup and resize
 */

import { useRef, useEffect, useState, useCallback } from 'react';

export interface UseCanvasReturn {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  context: CanvasRenderingContext2D | null;
  dimensions: { width: number; height: number };
}

export function useCanvas(): UseCanvasReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  // Initialize with window dimensions immediately
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });

  const handleResize = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;

    // Use window dimensions directly for full-screen canvas
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Set the canvas size accounting for device pixel ratio
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    // Scale the context to match device pixel ratio
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      setContext(ctx);
    }

    setDimensions({
      width,
      height,
    });
  }, []);

  // Initialize canvas and set up resize listener
  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return {
    canvasRef,
    context,
    dimensions,
  };
}
